/**
 * Отбор каталога гардероба в виде query-параметров.
 *
 * Единственное место, где живут имена параметров ссылки на каталог. Имена совпадают
 * с именами параметров API (`categoryIds`, `sortBy`, …), поэтому ссылка читается
 * глазами и ложится в запрос без перевода, а страница вещи умеет собрать «Назад к
 * результатам», ничего не зная о внутреннем состоянии списка.
 */

/** Фильтры-множества: каждый отдаётся повторяющимся ключом (`categoryIds=a&categoryIds=b`). */
export const LIST_FILTER_KEYS = [
    'categoryIds',
    'purposes',
    'colorIds',
    'sizeIds',
    'seasonIds',
    'conditionIds',
    'materialIds',
    'layerIds',
    'occasionIds',
    'styleIds',
    'weatherSuitabilityIds',
    'warmthLevelIds',
    'brandIds',
] as const;

export type ListFilterKey = typeof LIST_FILTER_KEYS[number];

export type ListFilters = Record<ListFilterKey, string[]>;

export const DEFAULT_SORT_BY = 'createdAt';
export const DEFAULT_SORT_DIR: 'asc' | 'desc' = 'desc';

/** Пустой набор — новый объект на каждый вызов, чтобы состояние страницы не делило ссылки. */
export function emptyListFilters(): ListFilters {
    return LIST_FILTER_KEYS.reduce((acc, key) => {
        acc[key] = [];
        return acc;
    }, {} as ListFilters);
}

export function hasAnyListFilter(lists: ListFilters): boolean {
    return LIST_FILTER_KEYS.some(key => lists[key].length > 0);
}

export interface CatalogUrlState {
    search: string;
    sortBy: string;
    sortDir: 'asc' | 'desc';
    lists: ListFilters;
    hasDefects: boolean;
    waterProof: boolean | null;
    /** Deep-link аналитики: «не носили дольше N дней». */
    notWornDays: number | null;
    /** Deep-link аналитики: «требуют внимания». */
    attention: boolean;
    /** «Только избранные» (CTHS-273): двухпозиционный — включён либо не задан. */
    favorite: boolean;
}

/**
 * Собирает канонический query отбора. Дефолты не пишутся — ссылка на каталог без
 * фильтров остаётся `/clothing`, а порядок ключей всегда один и тот же, так что
 * одинаковый отбор даёт посимвольно одинаковую ссылку.
 */
export function buildCatalogParams(state: Partial<CatalogUrlState>): URLSearchParams {
    const params = new URLSearchParams();
    if (state.search) params.set('search', state.search);
    if (state.sortBy && state.sortBy !== DEFAULT_SORT_BY) params.set('sortBy', state.sortBy);
    if (state.sortDir && state.sortDir !== DEFAULT_SORT_DIR) params.set('sortDir', state.sortDir);
    const lists = state.lists;
    if (lists) {
        LIST_FILTER_KEYS.forEach(key => {
            (lists[key] ?? []).forEach(value => params.append(key, value));
        });
    }
    if (state.hasDefects) params.set('hasDefects', 'true');
    if (state.waterProof != null) params.set('waterProof', String(state.waterProof));
    if (state.notWornDays != null && state.notWornDays > 0) params.set('notWornDays', String(state.notWornDays));
    if (state.attention) params.set('attention', 'true');
    if (state.favorite) params.set('favorite', 'true');
    return params;
}

/** Разобранная ссылка: `null` значит «в ссылке ничего не сказано», а не «выключено». */
export interface ParsedCatalogParams {
    search: string | null;
    sortBy: string | null;
    sortDir: 'asc' | 'desc' | null;
    lists: ListFilters;
    hasListFilters: boolean;
    hasDefects: boolean | null;
    waterProof: boolean | null;
    notWornDays: number | null;
    attention: boolean;
    favorite: boolean;
}

/**
 * Читает отбор из ссылки. Значение может прийти и повторяющимся ключом, и списком
 * через запятую — принимаем оба вида. Невалидное молча игнорируется: ссылка из чужих
 * рук не должна ронять страницу.
 */
export function parseCatalogParams(params: URLSearchParams): ParsedCatalogParams {
    const lists = emptyListFilters();
    let hasListFilters = false;
    LIST_FILTER_KEYS.forEach(key => {
        const values = params.getAll(key)
            .flatMap(value => value.split(','))
            .map(value => value.trim())
            .filter(Boolean);
        if (values.length > 0) {
            lists[key] = values;
            hasListFilters = true;
        }
    });

    const rawNotWorn = params.get('notWornDays');
    const notWornDays = rawNotWorn != null ? Number.parseInt(rawNotWorn, 10) : Number.NaN;
    const rawWaterProof = params.get('waterProof');
    const rawDefects = params.get('hasDefects');

    return {
        search: params.get('search'),
        sortBy: params.get('sortBy'),
        sortDir: params.get('sortDir') === 'asc' ? 'asc' : params.get('sortDir') === 'desc' ? 'desc' : null,
        lists,
        hasListFilters,
        hasDefects: rawDefects == null ? null : rawDefects === 'true',
        waterProof: rawWaterProof === 'true' ? true : rawWaterProof === 'false' ? false : null,
        notWornDays: Number.isFinite(notWornDays) && notWornDays > 0 ? notWornDays : null,
        attention: params.get('attention') === 'true',
        favorite: params.get('favorite') === 'true',
    };
}
